import { useFormAction, useNavigation } from '@remix-run/react'
import { clsx, type ClassValue } from 'clsx'
import { format, isPast, parse, startOfHour, startOfToday } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSpinDelay } from 'spin-delay'
import { extendTailwindMerge } from 'tailwind-merge'
import { extendedTheme } from './extended-theme.ts'

export function getUserImgSrc(imageId?: string | null) {
	return imageId ? `/resources/user-images/${imageId}` : '/img/user.png'
}
export function getTeamMemberImgSrc(imageId?: string | null) {
	return imageId ? `/resources/teammember-images/${imageId}` : '/img/user.png'
}

export function getVendorImgSrc(imageId?: string | null) {
	return imageId ? `/resources/vendor-images/${imageId}` : '/img/user.png'
}

export function getCompanyImgSrc(imageId?: string | null) {
	return imageId ? `/resources/company-images/${imageId}` : '/img/user.png'
}

export function getTimelineItemImgSrc(imageId?: string | null) {
	return imageId
		? `/resources/timeline-item-images/${imageId}`
		: '/img/user.png'
}

export function getNoteImgSrc(imageId: string) {
	return `/resources/note-images/${imageId}`
}

export function getEventContractSrc(fileId?: string | null) {
	return fileId ? `/resources/file/${fileId}` : ''
}

export function getErrorMessage(error: unknown) {
	if (typeof error === 'string') return error
	if (
		error &&
		typeof error === 'object' &&
		'message' in error &&
		typeof error.message === 'string'
	) {
		return error.message
	}
	console.error('Unable to get error message for error', error)
	return 'Unknown Error'
}

function formatColors() {
	const colors = []
	for (const [key, color] of Object.entries(extendedTheme.colors)) {
		if (typeof color === 'string') {
			colors.push(key)
		} else {
			const colorGroup = Object.keys(color).map(subKey =>
				subKey === 'DEFAULT' ? '' : subKey,
			)
			colors.push({ [key]: colorGroup })
		}
	}
	return colors
}

const customTwMerge = extendTailwindMerge<string, string>({
	extend: {
		theme: {
			colors: formatColors(),
			borderRadius: Object.keys(extendedTheme.borderRadius),
		},
		classGroups: {
			'font-size': [
				{
					text: Object.keys(extendedTheme.fontSize),
				},
			],
		},
	},
})

export function cn(...inputs: ClassValue[]) {
	return customTwMerge(clsx(inputs))
}

export function getDomainUrl(request: Request) {
	const host =
		request.headers.get('X-Forwarded-Host') ??
		request.headers.get('host') ??
		new URL(request.url).host
	const protocol = request.headers.get('X-Forwarded-Proto') ?? 'http'
	return `${protocol}://${host}`
}

export function getReferrerRoute(request: Request) {
	// spelling errors and whatever makes this annoyingly inconsistent
	// in my own testing, `referer` returned the right value, but 🤷‍♂️
	const referrer =
		request.headers.get('referer') ??
		request.headers.get('referrer') ??
		request.referrer
	const domain = getDomainUrl(request)
	if (referrer?.startsWith(domain)) {
		return referrer.slice(domain.length)
	} else {
		return '/'
	}
}

/**
 * Merge multiple headers objects into one (uses set so headers are overridden)
 */
export function mergeHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const merged = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			merged.set(key, value)
		}
	}
	return merged
}

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const combined = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			combined.append(key, value)
		}
	}
	return combined
}

export function invariant(
	condition: any,
	message: string | (() => string),
): asserts condition {
	if (!condition) {
		throw new Error(typeof message === 'function' ? message() : message)
	}
}

/**
 * Provide a condition and if that condition is falsey, this throws a 400
 * Response with the given message.
 *
 * inspired by invariant from 'tiny-invariant'
 *
 * @example
 * invariantResponse(typeof value === 'string', `value must be a string`)
 *
 * @param condition The condition to check
 * @param message The message to throw (or a callback to generate the message)
 * @param responseInit Additional response init options if a response is thrown
 *
 * @throws {Response} if condition is falsey
 */
export function invariantResponse(
	condition: any,
	message: string | (() => string),
	responseInit?: ResponseInit,
): asserts condition {
	if (!condition) {
		throw new Response(typeof message === 'function' ? message() : message, {
			status: 400,
			...responseInit,
		})
	}
}

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
	...responseInits: Array<ResponseInit | null | undefined>
) {
	let combined: ResponseInit = {}
	for (const responseInit of responseInits) {
		combined = {
			...responseInit,
			headers: combineHeaders(combined.headers, responseInit?.headers),
		}
	}
	return combined
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
	formAction,
	formMethod = 'POST',
	state = 'non-idle',
}: {
	formAction?: string
	formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
	state?: 'submitting' | 'loading' | 'non-idle'
} = {}) {
	const contextualFormAction = useFormAction()
	const navigation = useNavigation()
	const isPendingState =
		state === 'non-idle'
			? navigation.state !== 'idle'
			: navigation.state === state
	return (
		isPendingState &&
		navigation.formAction === (formAction ?? contextualFormAction) &&
		navigation.formMethod === formMethod
	)
}

/**
 * This combines useSpinDelay (from https://npm.im/spin-delay) and useIsPending
 * from our own utilities to give you a nice way to show a loading spinner for
 * a minimum amount of time, even if the request finishes right after the delay.
 *
 * This avoids a flash of loading state regardless of how fast or slow the
 * request is.
 */
export function useDelayedIsPending({
	formAction,
	formMethod,
	delay = 400,
	minDuration = 300,
}: Parameters<typeof useIsPending>[0] &
	Parameters<typeof useSpinDelay>[1] = {}) {
	const isPending = useIsPending({ formAction, formMethod })
	const delayedIsPending = useSpinDelay(isPending, {
		delay,
		minDuration,
	})
	return delayedIsPending
}

function callAll<Args extends Array<unknown>>(
	...fns: Array<((...args: Args) => unknown) | undefined>
) {
	return (...args: Args) => fns.forEach(fn => fn?.(...args))
}

/**
 * Use this hook with a button and it will make it so the first click sets a
 * `doubleCheck` state to true, and the second click will actually trigger the
 * `onClick` handler. This allows you to have a button that can be like a
 * "are you sure?" experience for the user before doing destructive operations.
 */
export function useDoubleCheck() {
	const [doubleCheck, setDoubleCheck] = useState(false)

	function getButtonProps(
		props?: React.ButtonHTMLAttributes<HTMLButtonElement>,
	) {
		const onBlur: React.ButtonHTMLAttributes<HTMLButtonElement>['onBlur'] =
			() => setDoubleCheck(false)

		const onClick: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'] =
			doubleCheck
				? undefined
				: e => {
						e.preventDefault()
						setDoubleCheck(true)
					}

		const onKeyUp: React.ButtonHTMLAttributes<HTMLButtonElement>['onKeyUp'] =
			e => {
				if (e.key === 'Escape') {
					setDoubleCheck(false)
				}
			}

		return {
			...props,
			onBlur: callAll(onBlur, props?.onBlur),
			onClick: callAll(onClick, props?.onClick),
			onKeyUp: callAll(onKeyUp, props?.onKeyUp),
		}
	}

	return { doubleCheck, getButtonProps }
}

/**
 * Simple debounce implementation
 */
function debounce<Callback extends (...args: Parameters<Callback>) => void>(
	fn: Callback,
	delay: number,
) {
	let timer: ReturnType<typeof setTimeout> | null = null
	return (...args: Parameters<Callback>) => {
		if (timer) clearTimeout(timer)
		timer = setTimeout(() => {
			fn(...args)
		}, delay)
	}
}

/**
 * Debounce a callback function
 */
export function useDebounce<
	Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
	const callbackRef = useRef(callback)
	useEffect(() => {
		callbackRef.current = callback
	})
	return useMemo(
		() =>
			debounce(
				(...args: Parameters<Callback>) => callbackRef.current(...args),
				delay,
			),
		[delay],
	)
}

export function useDebounceValue<T>(value: T, delay: number): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

export async function downloadFile(url: string, retries: number = 0) {
	const MAX_RETRIES = 3
	try {
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`Failed to fetch image with status ${response.status}`)
		}
		const contentType = response.headers.get('content-type') ?? 'image/jpg'
		const blob = Buffer.from(await response.arrayBuffer())
		return { contentType, blob }
	} catch (e) {
		if (retries > MAX_RETRIES) throw e
		return downloadFile(url, retries + 1)
	}
}

///

export function getFirstCharacter(inputString: string): string {
	return inputString.charAt(0)
}

export function sortByDate(
	objects: {
		date: string
		name: string
		type: string
		id: string
	}[],
): { date: string; name: string; type: string; id: string }[] {
	// Sort the array based on the "date" property
	objects.sort(
		(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
	)

	// Return the sorted array
	return objects
}

export function sortTimelineItems(timelineItems: any[]) {
	// Use the Array.prototype.sort() method to sort the items
	timelineItems.sort((a, b) => {
		// Compare dates first
		const dateA = new Date(a.date)
		const dateB = new Date(b.date)
		if (dateA < dateB) {
			return -1
		}
		if (dateA > dateB) {
			return 1
		}

		// If dates are equal, compare times
		const timeA = convertTimeTo24HourFormat(a.time)
		const timeB = convertTimeTo24HourFormat(b.time)
		if (timeA < timeB) {
			return -1
		}
		if (timeA > timeB) {
			return 1
		}

		// If both dates and times are equal, preserve the existing order
		return 0
	})

	return timelineItems
}

// Helper function to convert time from 12-hour format to 24-hour format for comparison
function convertTimeTo24HourFormat(time: string): string {
	const [hours, minutes] = time.replace(/[^\d:]/g, '').split(':')
	const meridian = time.slice(-2).toLowerCase()

	let hours24 = parseInt(hours || '', 10)
	if (meridian === 'am' && hours24 === 12) {
		hours24 = 0
	} else if (meridian === 'pm' && hours24 !== 12) {
		hours24 += 12
	}

	return `${hours24.toString().padStart(2, '0')}:${minutes}`
}

const today = startOfToday()
export const formattedToday = format(today, 'yyyy-MM-dd')

export const titleMaxLength = 100
export const contentMaxLength = 10000

export const phoneRegex =
	/^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/

export const dateRegex = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/

export const timeRegex = /^(1[0-2]|0?[1-9]):[0-5][0-9][ap]m$/

export const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/

export function centsToDollarFormat(cents: number): string {
	if (isNaN(cents) || cents < 0) {
		throw new Error('Invalid cents value provided')
	}
	const dollars = Math.floor(cents / 100)
	const remainingCents = cents % 100
	return `${dollars}.${remainingCents < 10 ? '0' : ''}${remainingCents}`
}

export function convertToDateISO(dateTimeStr: string): string {
	let inputDate = dateTimeStr

	// Check if the format is YYYY-MM-DD
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeStr)) {
		inputDate = dateTimeStr + 'T00:00:00.000Z'
	}

	const date = new Date(inputDate)

	if (isNaN(date.getTime())) {
		throw new Error(
			'Invalid date format. Expected format: YYYY-MM-DD or a full ISO-8601 string',
		)
	}

	return date.toISOString()
}

export function formatDateToYYYYMMDD(isoDate?: string): string {
	if (!isoDate) {
		return ''
	}

	const date = new Date(isoDate)

	// Check if date is invalid (e.g. isoDate = "invalid-date-string")
	if (isNaN(date.getTime())) {
		return ''
	}

	// Get year, month, and day
	const year = date.getUTCFullYear()
	const month = (date.getUTCMonth() + 1).toString().padStart(2, '0') // Month is 0-indexed, so add 1
	const day = date.getUTCDate().toString().padStart(2, '0')

	return `${year}-${month}-${day}`
}

export function formatToPrettyDateString(eventdate: Date | string): string {
	const date = eventdate instanceof Date ? eventdate : new Date(eventdate)

	// Array of month names
	const months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	]

	// Extract year, month, and day
	const year = date.getUTCFullYear()
	const monthName = months[date.getUTCMonth()]
	const day = date.getUTCDate()

	return `${monthName} ${day}, ${year}`
}

export const isEventToday = (eventDate: string) => {
	const eventDateParsed = new Date(eventDate) // This already parses the date in UTC

	// Get the UTC date components
	const eventYear = eventDateParsed.getUTCFullYear()
	const eventMonth = eventDateParsed.getUTCMonth()
	const eventDay = eventDateParsed.getUTCDate()

	// Get today's date in UTC components
	const todayUTC = new Date() // This is in local time
	const todayYear = todayUTC.getUTCFullYear()
	const todayMonth = todayUTC.getUTCMonth()
	const todayDay = todayUTC.getUTCDate()

	// Compare year, month, and day components in UTC
	return (
		eventYear === todayYear &&
		eventMonth === todayMonth &&
		eventDay === todayDay
	)
}

export const isEventPast = (eventDate: string) => {
	const eventDateParsed = new Date(eventDate) // Parses the date in UTC

	// Get the UTC date components of the event
	const eventYear = eventDateParsed.getUTCFullYear()
	const eventMonth = eventDateParsed.getUTCMonth()
	const eventDay = eventDateParsed.getUTCDate()

	// Get today's date components in UTC
	const todayUTC = new Date() // This is in local time converted to UTC
	const todayYear = todayUTC.getUTCFullYear()
	const todayMonth = todayUTC.getUTCMonth()
	const todayDay = todayUTC.getUTCDate()

	// Create UTC date objects for both today and event day at the start of the day for comparison
	const eventDateUTC = new Date(Date.UTC(eventYear, eventMonth, eventDay))
	const todayDateUTC = new Date(Date.UTC(todayYear, todayMonth, todayDay))

	// Compare if the event date is before today's date
	return eventDateUTC < todayDateUTC
}

export function isTimePast(time: string) {
	// Get the current date and time
	const currentDate = new Date()

	// Parse the provided time using the format "h:mma"
	const parsedTime = parse(time, 'HH:mm', currentDate)

	// Check if the current time is past the provided time
	const result = isPast(parsedTime)

	return result
}

export function isCurrentHour(time: string) {
	const currentTime = new Date()
	const parsedTime = parse(time.toLowerCase(), 'HH:mm', new Date())

	const currentHour = startOfHour(currentTime)
	const inputTime = startOfHour(parsedTime)

	return currentHour.getTime() === inputTime.getTime()
}

export function convertTo12Hour(time: string) {
	// Split the time based on the colon
	const [hour, minute] = time.split(':').map(Number)

	// Determine AM or PM suffix
	// @ts-ignore
	const period = hour >= 12 ? 'PM' : 'AM'

	// Adjust the hour for 12-hour format
	// @ts-ignore
	const hour12 = hour % 12 === 0 ? 12 : hour % 12

	// Format the minute with leading zero if needed
	// @ts-ignore
	const formattedMinute = minute < 10 ? `0${minute}` : minute

	// Combine into a final formatted time
	return `${hour12}:${formattedMinute} ${period}`
}

export function formatDayAndDateUTC(isoDate: string) {
	// Parse the ISO date string into a Date object
	const date = new Date(isoDate)

	// Use toLocaleDateString with timeZone set to 'UTC' to format the date in UTC
	const dayOfWeek = date.toLocaleDateString('en-US', {
		weekday: 'short',
		timeZone: 'UTC',
	})
	const monthAndDay = date.toLocaleDateString('en-US', {
		month: 'numeric',
		day: 'numeric',
		timeZone: 'UTC',
	})

	// Combine and format as "Wed 5/15"
	return `${dayOfWeek} ${monthAndDay}`
}

export function getInitials(fullName: string): string {
	// Split the full name into a list of words
	const nameParts: string[] = fullName.trim().split(/\s+/)

	// Check if there are at least two parts to take initials from the first and last names
	if (nameParts.length >= 2) {
		// Extract the first letter of the first and last name parts
		// @ts-ignore
		const initials = nameParts[0][0] + nameParts[nameParts.length - 1][0]
		return initials.toUpperCase()
	}

	// If there's only one part or the name is oddly formatted, return the first letter of the first part
	if (nameParts.length === 1) {
		// @ts-ignore
		return nameParts[0][0].toUpperCase()
	}

	// Return an empty string if no valid name is provided
	return ''
}

export function getDefaultCurrencyCode(): string {
	// Use navigator.language for the browser environment or 'en-US' as a fallback
	const locale = navigator.language || 'en-US';
	const formatter = new Intl.NumberFormat(locale, {
			style: 'currency',
			currency: 'USD', // Default to USD, will be overridden by locale
	});

	// Use a hack to extract the currency code from the formatted number
	const parts = formatter.formatToParts(0);
	const currencyPart = parts.find(part => part.type === 'currency');
	return currencyPart ? currencyPart.value : 'USD';
}

export function printLocalCurrency(value: number, currencyCode: string): string {
	const formatter = new Intl.NumberFormat('default', {
			style: 'currency',
			currency: currencyCode,
	});

	return formatter.format(value);
}
